import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  effect,
  inject,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  ValidatorFn,
} from '@angular/forms';
import { distinctUntilChanged } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { dropdownAnimation } from '@animations/dropdown.animations';
import { ValueChangeEvent } from '@interfaces/form.interface';
import { ThousandPipe } from '@pipes/thousand.pipe';
import { NgxTippyProps } from 'ngx-tippy-wrapper';
import { IonicService } from '@arkius/shared-services';
import { Platform } from '@ionic/angular';
import { Keyboard } from '@capacitor/keyboard';
import { environment } from '@shared-envs';

@Component({
  selector: 'ark-input',
  templateUrl: './input.component.html',
  animations: [dropdownAnimation()],
  styleUrls: ['./input.component.scss'],
})

/**
 * @name InputComponent
 * @description Functional form component for Input Text, Password, Emails etc.
 * @property label<string> - The floating label text of the input field
 * @property name<string> - Passed as a name tag to the form element
 * @property toolTip<string> - Text to display when hovering over the lock icon. Works only if locked = true
 * @property tabIndex<number | 'auto'> - Determines the element's tab index
 * @property noTab<boolean> - Determines if the element is included in the tab order
 * @property hasFocus<boolean> - If set to true, it initializes with having focus. Use only one per page
 * @property pattern<string> - RegExp pattern for value validation
 * @property errorLabel<string> - External error label (from server events)
 * @property type<string> - The element's content type. Determines behavior and validation
 * @property disabled<boolean> - If set to true, the element is read-only
 * @property validateOn<'blur' | 'change'> - Determines if content validation will happen while typing or when focus is lost.
 * @property info<string> - The text content of the information line below the element
 * @property locked<boolean> - If true the element is read only and a lock icon appears with a tooltip on hover.
 * @property required<boolean> - If true the element will be marked as invalid if not filled
 * @property maxlength<number> - The maximum characters allowed to be typed in the element
 * @property verifying<boolean> - Toggles the verifying icon animation, useful for server queries
 * @property error<boolean> - If true forces a custom error display. Works in conjunction with errorLabel
 * @property value<string> - Sets a predefined value for the element
 * @emits valueCleared<{value:string,status:string}> - Fires whenever the user clears the element value
 * @emits valueChanged<{value:string,status:string}> - Fires whenever the element value is changed
 * @emits lostFocus<{value:string,status:string}> - Fires whenever the element loses focus
 * @implements OnInit, OnDestroy
 * @author Sotiris Varotsis
 */
export class InputComponent implements OnInit, OnDestroy {
  @ViewChild('input', { static: true })
  input!: ElementRef;

  @ViewChild('inputWrapper', { static: true })
  inputWrapper!: ElementRef;

  @ViewChild('ionInput', { static: true })
  ionInput!: ElementRef;

  platform = inject(Platform);

  /**
   * Use formStates to programmatically control the form element's state
   * These are used as CSS class definitions as well.
   * Copy them over to other form elements
   * @property disabled - When the element is disabled
   * @property labelRaised - When the floating label needs to be raised
   * @property focused - When the element has focused
   * @property error - When the element has an error
   * @property valid - When the value of the element is valid
   * @property selected - Used for radio and checkboxes
   */

  formStates = {
    disabled: false,
    labelRaised: false,
    focused: false,
    error: false,
    valid: false,
    selected: false,
  };

  /**
   * displayIcon is only used for input fields that have additional icons
   * such as clear, validation, password reveal and spinners
   */

  displayIcon = '';

  passwordVisible = false;
  passwordRegx: RegExp =
    /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
  @Input() passwordValidate = false;
  errorMessage = '';

  form: UntypedFormGroup = new UntypedFormGroup({});
  formSubscription: Subscription = new Subscription();
  statusSubscription: Subscription = new Subscription();

  formValidators: ValidatorFn[] = [];

  @Input() label = '';
  @Input() ariaLabel?: string;
  @Input() labelLines = 1;
  @Input() labelHtml!: string;
  @Input() placeholder = '';
  @Input() name = '';
  @Input() toolTip = 'Cannot change content';
  @Input() tippyName = 'tippy-input';
  @Input() public toolOptions: NgxTippyProps = {
    placement: 'bottom',
    trigger: 'mouseenter focus',
    allowHTML: true,
    interactive: true,
    theme: 'arkius',
    arrow: true,
  };
  @Input() tabIndex: any = 'auto';
  @Input() noTab = false;
  @Input() hasFocus = false;
  @Input() readonly = false;
  @Input() pattern: any = '';
  @Input() type: 'text' | 'password' | 'email' | 'decimal' = 'text';
  @Input() disabled = false;
  @Input() validateOn: 'blur' | 'change' = 'change';
  @Input() info = '';
  @Input() locked = false;
  @Input() required = false;
  @Input() maxlength = 524288;
  @Input() min = -1000; // For type number
  @Input() max = -1000; // For type number
  @Input() minimumChars = -1;
  @Input() maximumChars = -1;
  @Input() isNumber: boolean = false;
  @Input() rightIcon = '';
  @Input() checkMinChar = false;
  @Input() clearOnEnterPressed = false;
  @Input() preventEnterNewLine = false;
  @Input() enterkeyhint: string | undefined;
  @Input() actionIcon?: string;
  @Input() mentionItems: string[] = [];
  @Input() size: 'small' | 'medium' | 'large' | 'auto' = 'medium';
  @Output() mentionSelected = new EventEmitter<string>();
  @Output() searchTerm = new EventEmitter<string>();
  @Input() hideIcons = false;

  _verifying = false;

  get formFilled() {
    return this.form.get('formValue')?.value;
  }

  get verifying(): boolean {
    return this._verifying;
  }

  @Input() set verifying(value: boolean) {
    this._verifying = value;
    this.handleIcon();
  }

  _errorLabel = '';

  get errorLabel(): string {
    return this._errorLabel;
  }

  @Input() set errorLabel(value: string) {
    this._errorLabel = value;
    if (value) {
      this.errorMessage = value;
    }
  }

  _error = false;

  get error(): boolean {
    return this._error;
  }

  @Input() set error(value: boolean) {
    this._error = value;
    this.formStates.error = value;
    if (value) {
      this.form.setErrors({ incorrect: true });
      this.form.markAsTouched();
    } else {
      this.form.setErrors(null);
    }
  }

  _value: string | number | null | undefined = null;
  get value(): string | number | null | undefined {
    return this._value;
  }

  @Input() set value(value: string | number | null | undefined) {
    this._value = value;
    if (this.form.controls['formValue']) {
      this.form.controls['formValue'].setValue(this.value);
      if (value) {
        this.form.controls['formValue'].markAsDirty();

        // Update the formStates in case the value comes asynchronously
        // and not during the component initialization.
        if (!this.formStates.labelRaised) {
          this.formStates.labelRaised = true;
        }
        if (!this.formStates.valid) {
          this.formStates.valid = true;
        }
      } else {
        this.form.controls['formValue'].markAsPristine();
      }
    }
  }

  @Output() inputted = new EventEmitter<any>();
  @Output() keyPressed = new EventEmitter<any>();
  @Output() valueCleared: EventEmitter<any> = new EventEmitter<any>();
  @Output() valueChanged: EventEmitter<ValueChangeEvent> =
    new EventEmitter<ValueChangeEvent>();
  @Output() lostFocus: EventEmitter<ValueChangeEvent> =
    new EventEmitter<ValueChangeEvent>();
  @Output() formErrors: EventEmitter<ValueChangeEvent> =
    new EventEmitter<ValueChangeEvent>();
  @Output() enterPressed: EventEmitter<ValueChangeEvent> =
    new EventEmitter<ValueChangeEvent>();
  @Output() actionIconPressed = new EventEmitter<{
    value: string;
    status: string;
  }>();
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() focus: EventEmitter<unknown> = new EventEmitter<unknown>();
  valueClearedObservable = this.valueChanged.asObservable();
  valueChangedObservable = this.valueChanged.asObservable();
  lostFocusChangedObservable = this.valueChanged.asObservable();

  constructor(
    private fb: UntypedFormBuilder,
    public translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.handleKeyboard();
    if (this.required) {
      this.formValidators.push(Validators.required);
    }
    if (this.type === 'email') {
      this.formValidators.push(Validators.email);
    }

    if (this.passwordValidate) {
      this.formValidators.push(Validators.pattern(this.passwordRegx));
    }

    if (this.minimumChars > -1) {
      this.formValidators.push(Validators.minLength(this.minimumChars));
    }

    if (this.maximumChars > -1) {
      this.formValidators.push(Validators.maxLength(this.maximumChars));
    }

    if (this.min > -1000) {
      this.formValidators.push(Validators.min(this.min));
    }

    if (this.max > -1000) {
      this.formValidators.push(Validators.max(this.max));
    }

    if (this.isNumber) {
      this.formValidators.push(Validators.pattern(/^\d+$/));
    }

    const validators = this.formValidators;
    this.form = this.fb.group({
      formValue: new UntypedFormControl('', {
        validators,
        updateOn: this.validateOn === 'change' ? 'change' : 'blur',
      }),
    });

    if (this.value) {
      this.form.controls['formValue'].setValue(this.value);
      this.form.controls['formValue'].markAsDirty();
      this.formStates.labelRaised = true;
      this.formStates.valid = true;
    } else {
      this.form.controls['formValue'].markAsPristine();
    }

    if (this.locked) {
      this.disabled = true;
      this.formStates.disabled = true;
    }

    if (this.disabled) {
      this.formStates.disabled = true;
      this.tabIndex = -1;
    }

    if (this.hasFocus) {
      this.input.nativeElement.focus();
    }

    this.handleIcon();
    this.formSubscription = this.form.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe(() => {
        this.errorMessage =
          this.errorLabel || this.translate.instant('COMPONENTS.INPUT.ERROR');
        this.formStates.error = false;
        if (this.form.controls['formValue'].errors?.['email']) {
          this.formStates.error = true;
          this.errorMessage = this.translate.instant(
            'COMPONENTS.INPUT.ERROR_INVALID_EMAIL',
          );
        }
        if (this.form.controls['formValue'].errors?.['required']) {
          this.formStates.error = true;
          this.formStates.error = true;
          this.errorMessage =
            this._errorLabel ||
            this.translate.instant('COMPONENTS.INPUT.ERROR_REQUIRED');
        }
        if (!this.form.controls['formValue'].value && !this.required) {
          this.form.controls['formValue'].markAsPristine();
        }

        if (this.form.controls['formValue'].errors?.['maxlength']) {
          this.formStates.error = true;
          this.errorMessage = this.translate.instant(
            'COMPONENTS.INPUT.ERROR_TOO_MANY_CHARS',
            { value: new ThousandPipe().transform(this.maximumChars) },
          );
        }

        if (
          this.checkMinChar &&
          this.form.controls['formValue'].errors?.['minlength']
        ) {
          this.formStates.error = true;
          this.errorMessage = this.translate.instant(
            'COMPONENTS.INPUT.ERROR_TOO_FEW_CHARS',
            { value: new ThousandPipe().transform(this.minimumChars) },
          );
        }

        if (
          this.form.controls['formValue'].errors?.['min'] &&
          this.min > -1000
        ) {
          this.formStates.error = true;
          this.errorMessage = this.translate.instant(
            'COMPONENTS.INPUT.ERROR_MIN_VALUE',
            { value: new ThousandPipe().transform(this.min) },
          );
        }

        if (
          this.form.controls['formValue'].errors?.['max'] &&
          this.max > -1000
        ) {
          this.formStates.error = true;
          this.errorMessage = this.translate.instant(
            'COMPONENTS.INPUT.ERROR_MAX_VALUE',
            { value: new ThousandPipe().transform(this.max) },
          );
        }

        if (
          this.form.controls['formValue'].errors?.['pattern'] &&
          this.isNumber
        ) {
          this.formStates.error = true;
          this.errorMessage = this.translate.instant(
            'COMPONENTS.INPUT.ERROR_NUMBER',
            { value: new ThousandPipe().transform(this.max) },
          );
        }

        if (
          this.type === 'password' &&
          this.passwordValidate &&
          this.form.controls['formValue'].errors
        ) {
          this.formStates.error = true;
          const hasNumber = /\d/.test(this.form.controls['formValue'].value);
          const hasUpper = /[A-Z]/.test(this.form.controls['formValue'].value);
          const hasLower = /[a-z]/.test(this.form.controls['formValue'].value);
          const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/.test(
            this.form.controls['formValue'].value,
          );
          const hasMin = /^.{8,}$/.test(this.form.controls['formValue'].value);

          const errors = {
            number: !hasNumber || null,
            upper: !hasUpper || null,
            lower: !hasLower || null,
            special: !hasSpecial || null,
            min: !hasMin || null,
          };
          this.form.get('formValue')?.setErrors(errors);

          this.errorMessage = '';
        }

        if (this._error) {
          this.formStates.error = true;
          this.errorMessage =
            this._errorLabel ||
            this.errorMessage ||
            this.translate.instant('COMPONENTS.INPUT.ERROR');
        }

        this.handleIcon();

        if (this.form.controls['formValue'].errors) {
          this.formErrors.emit({
            value: this.form.controls['formValue'].errors,
            status: 'INVALID',
          });
        } else {
          this.formErrors.emit({
            value: this.form.controls['formValue'].errors,
            status: 'VALID',
          });
        }
        this.valueChanged.emit({
          value: this.form.controls['formValue'].value,
          status: this.form.controls['formValue'].status,
        });
      });

    this.statusSubscription = this.form.statusChanges.subscribe((status) => {
      this.formStates.valid = status === 'VALID';
      this.handleIcon();
    });
  }

  onMentionSelect(event: string) {
    environment.log('Mention selected:', event);
    this.mentionSelected.emit(event);
  }

  onMentionClosed() {
    // Reset the mention items
    this.mentionItems = [];
  }

  formStatesReset() {
    this.formStates.disabled = false;
    this.formStates.error = false;
    this.formStates.focused = false;
    this.formStates.labelRaised = false;
    this.formStates.selected = false;
    this.formStates.valid = false;
  }

  disableTab(evt: any): void {
    evt.preventDefault();
  }

  onInput(event: any) {
    this.inputted.emit(event);
  }

  mentionInputHandler(event: any) {
    this.searchTerm.emit(event);
  }

  onKeyDown(event: any) {
    this.keyPressed.emit(event);
  }

  onEnterPressed(event: Event): void {
    if (this.preventEnterNewLine) {
      event.preventDefault();
    }
    const value = this.form.controls['formValue'].value;
    if (!value || value.length <= 0) return;
    this.enterPressed.emit({
      value: this.form.controls['formValue'].value,
      status: this.form.controls['formValue'].status,
    });

    if (this.clearOnEnterPressed) {
      this.clear();
    }
  }

  togglePasswordVisibility(e: Event): void {
    e.preventDefault();
    e.stopPropagation();
    this.passwordVisible = !this.passwordVisible;
  }

  clear(e?: Event): void {
    e?.preventDefault();
    e?.stopPropagation();
    if (this.input) this.input.nativeElement.value = '';
    if (this.form.controls['formValue'])
      this.form.controls['formValue'].setValue('');
    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.handleIcon();
    this.valueCleared.emit();
  }

  actionIconClick() {
    const value = this.form.controls['formValue'].value;
    if (!value || value.length <= 0) return;
    this.enterPressed.emit({
      value: this.form.controls['formValue'].value,
      status: this.form.controls['formValue'].status,
    });

    this.actionIconPressed.emit({
      value,
      status: this.form.controls['formValue'].status,
    });

    if (this.clearOnEnterPressed) {
      this.clear();
    }
    this.formStatesReset();
  }

  /**
   * getFormClasses()
   * @description constructs the form class object based on the element's states
   * @returns string
   */

  getFormClasses(): string {
    const classArray = [];
    for (const [key, value] of Object.entries(this.formStates)) {
      if (value) {
        classArray.push(key);
      }
    }
    return classArray.join(' ');
  }

  handleIcon(): void {
    if (this.hideIcons) {
      this.displayIcon = '';
      return;
    }

    if (this.verifying) {
      this.displayIcon = '';
    } else {
      if (this.type === 'password') {
        this.displayIcon = 'eye';
      } else {
        if (this.locked) {
          this.displayIcon = 'lock';
        } else {
          if (this.formStates.focused) {
            if (this.form.controls['formValue'].value) {
              this.displayIcon = 'clear';
            } else {
              this.displayIcon = '';
            }
          } else {
            if (this.formStates.valid) {
              this.displayIcon = 'tick';
            } else {
              this.displayIcon = '';
            }
          }
        }
      }
    }
  }

  handleKeyboard() {
    if (!this.platform.is('ios')) return;
    Keyboard.addListener('keyboardDidShow', (info) => {
      if (!this.formStates.focused) return;
      this.input.nativeElement.style.scrollMarginBottom = '50px';
      (this.input.nativeElement as HTMLElement).scrollIntoView({
        behavior: 'instant',
        block: 'nearest',
        inline: 'nearest',
      });
    });
  }
  /**
   * @name onFocus()
   * Handles events when the element gains focus. Copy this over to your form component.
   * labelRaised and focused formStates are necessary. The rest pertains to the icons
   */

  onFocus(): void {
    this.formStates.labelRaised = true;
    this.formStates.focused = true;
    if (this.type !== 'password') {
      if (this.form.controls['formValue'].value) {
        this.displayIcon = 'clear';
      } else {
        this.displayIcon = '';
      }
    } else {
      this.displayIcon = 'eye';
    }
    this.focus.emit();
  }

  /**
   * @name onBlur()
   * Handles events when the element loses focus. Copy this over to your form component.
   * labelRaised and focused formStates are necessary as well as the lostFocus emitted event. The rest pertains to the icons
   */

  onBlur(): void {
    const value = this.form.controls['formValue'].value;
    if (!value) {
      this.formStates.labelRaised = false;
    }
    this.formStates.focused = false;
    if (this.type !== 'password') {
      if (this.formStates.valid && value) {
        this.displayIcon = 'tick';
      } else {
        this.displayIcon = '';
      }
    } else {
      this.displayIcon = 'eye';
    }

    if (this.form.controls['formValue'].errors?.['minlength']) {
      this.formStates.error = true;
      this.errorMessage = this.translate.instant(
        'COMPONENTS.INPUT.ERROR_TOO_FEW_CHARS',
        { value: new ThousandPipe().transform(this.minimumChars) },
      );
    }

    if (this.form.controls['formValue'].errors?.['maxlength']) {
      this.formStates.error = true;
      this.errorMessage = this.translate.instant(
        'COMPONENTS.INPUT.ERROR_TOO_MANY_CHARS',
        { value: new ThousandPipe().transform(this.maximumChars) },
      );
    }

    if (this.form.controls['formValue'].errors?.['min'] && this.min > -1000) {
      this.formStates.error = true;
      this.errorMessage = this.translate.instant(
        'COMPONENTS.INPUT.ERROR_MIN_VALUE',
        { value: new ThousandPipe().transform(this.min) },
      );
    }

    if (this.form.controls['formValue'].errors?.['max'] && this.max > -1000) {
      this.formStates.error = true;
      this.errorMessage = this.translate.instant(
        'COMPONENTS.INPUT.ERROR_MAX_VALUE',
        { value: new ThousandPipe().transform(this.max) },
      );
    }
    this.lostFocus.emit({
      value: this.form.controls['formValue'].value,
      status: this.form.controls['formValue'].status,
    });
  }

  /**
   * @name ngOnDestroy()
   * Make sure to always unsubscribe your RxJs subscriptions otherwise
   * bad things will happen
   */

  ngOnDestroy(): void {
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
    if (this.statusSubscription) {
      this.statusSubscription.unsubscribe();
    }
    this.clear();
  }
}
